import { useCallback, useEffect, useMemo, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { permissionsApi, rolesApi } from '@/api';
import {
  ROLE_DESCRIPTIONS,
  ROLE_PRESET_LABELS,
  ROLE_PRESET_PERMISSIONS,
  ROLES_HELP,
} from '@/constants/roles';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { useDebounce } from '@/hooks/useDebounce';
import { usePagination } from '@/hooks/usePagination';
import type { PermissionResponse, RoleRequest, RoleResponse } from '@/types/role';
import { usePermissions } from '@/hooks/usePermissions';
import { notify } from '@/core/toast/notify';
import { showApiError } from '@/utils/apiError';
const empty: RoleRequest = { name: '', code: '', permissionIds: [] };

const EDITABLE_SYSTEM_ROLE_CODES = ['ORGANIZATION_OWNER'] as const;

function isRoleEditable(role: RoleResponse) {
  return !role.systemRole || EDITABLE_SYSTEM_ROLE_CODES.includes(role.code as (typeof EDITABLE_SYSTEM_ROLE_CODES)[number]);
}

function groupPermissions(perms: PermissionResponse[]) {
  const groups = new Map<string, PermissionResponse[]>();
  for (const p of perms) {
    const module = p.module ?? 'OTHER';
    const list = groups.get(module) ?? [];
    list.push(p);
    groups.set(module, list);
  }
  return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
}

function PermissionList({ role }: { role: RoleResponse }) {
  const perms = role.permissions ?? [];
  if (perms.length === 0) {
    return <p className="text-sm text-slate-500">No permissions assigned.</p>;
  }
  const grouped = groupPermissions(perms);
  return (
    <div className="max-h-80 space-y-4 overflow-y-auto">
      {grouped.map(([module, items]) => (
        <div key={module}>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">{module}</p>
          <ul className="space-y-1">
            {items.map((p) => (
              <li
                key={p.id}
                className="rounded-lg bg-slate-50 px-3 py-2 text-sm dark:bg-slate-800"
              >
                <span className="font-medium text-slate-800 dark:text-slate-200">{p.name}</span>
                <span className="ml-2 text-xs text-slate-500">{p.code}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

export function RolesPage() {
  const { has } = usePermissions();
  const canManageRoles = has('ROLE_MANAGE');
  const pagination = usePagination();
  const debouncedSearch = useDebounce(pagination.search);
  const [data, setData] = useState<RoleResponse[]>([]);
  const [permissions, setPermissions] = useState<PermissionResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewing, setViewing] = useState<RoleResponse | null>(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [editing, setEditing] = useState<RoleResponse | null>(null);
  const [form, setForm] = useState<RoleRequest>(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    permissionsApi.list({ size: 200 }).then((p) => setPermissions(p.content)).catch(() => {});
  }, []);

  const assignablePermissions = useMemo(
    () => permissions.filter((p) => p.code !== 'MASTER_ADMIN' && p.module !== 'MASTER'),
    [permissions],
  );

  const permissionGroups = useMemo(
    () => groupPermissions(assignablePermissions),
    [assignablePermissions],
  );

  const applyPreset = (presetKey: string) => {
    const codes = ROLE_PRESET_PERMISSIONS[presetKey];
    if (!codes) return;
    const ids = assignablePermissions.filter((p) => codes.includes(p.code)).map((p) => p.id);
    setForm((f) => ({ ...f, permissionIds: ids }));
    notify.success('Preset applied — adjust if needed');
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await rolesApi.list({
        page: pagination.page,
        size: pagination.size,
        search: debouncedSearch || undefined,
      });
      setData(res.content);
      pagination.applyPageResponse(res);
    } catch (err) {
      showApiError(err, 'Failed to load roles');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.size, debouncedSearch, pagination]);

  useEffect(() => {
    load();
  }, [load]);

  const openView = async (role: RoleResponse) => {
    setViewOpen(true);
    setViewLoading(true);
    setViewing(role);
    try {
      const full = await rolesApi.get(role.id);
      setViewing(full);
    } catch (err) {
      showApiError(err, 'Failed to load role permissions');
    } finally {
      setViewLoading(false);
    }
  };

  const openEdit = async (role: RoleResponse) => {
    setEditing(role);
    setModalOpen(true);
    try {
      const full = await rolesApi.get(role.id);
      setForm({
        name: full.name,
        code: full.code,
        description: full.description,
        permissionIds: full.permissionIds ?? full.permissions?.map((p) => p.id) ?? [],
      });
    } catch (err) {
      showApiError(err, 'Failed to load role');
      setForm({
        name: role.name,
        code: role.code,
        description: role.description,
        permissionIds: role.permissionIds ?? [],
      });
    }
  };

  const togglePerm = (id: number) => {
    const ids = form.permissionIds ?? [];
    setForm({
      ...form,
      permissionIds: ids.includes(id) ? ids.filter((p) => p !== id) : [...ids, id],
    });
  };

  const columns: Column<RoleResponse>[] = [
    {
      key: 'type',
      header: 'Type',
      render: (r) =>
        r.systemRole ? (
          <span className="rounded bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300">
            Built-in
          </span>
        ) : (
          <span className="rounded bg-primary-50 px-2 py-0.5 text-xs font-medium text-primary-700 dark:bg-primary-950 dark:text-primary-300">
            Custom
          </span>
        ),
    },
    { key: 'name', header: 'Role' },
    { key: 'code', header: 'Code' },
    {
      key: 'summary',
      header: 'Purpose',
      render: (r) => (
        <span className="text-slate-600 dark:text-slate-400">
          {ROLE_DESCRIPTIONS[r.code] ?? r.description ?? '—'}
        </span>
      ),
    },
    {
      key: 'permissions',
      header: 'Permissions',
      render: (r) => r.permissions?.length ?? r.permissionIds?.length ?? 0,
    },
    {
      key: 'actions',
      header: '',
      render: (r) => (
        <div className="flex gap-2">
          <button
            type="button"
            className="flex items-center gap-1 text-primary-600"
            onClick={() => openView(r)}
          >
            <VisibilityIcon sx={{ fontSize: 16 }} />
            View
          </button>
          {canManageRoles && !r.systemRole && (
            <button type="button" className="text-primary-600" onClick={() => openEdit(r)}>
              Edit
            </button>
          )}
          {canManageRoles && r.systemRole && isRoleEditable(r) && (
            <button type="button" className="text-primary-600" onClick={() => openEdit(r)}>
              Edit permissions
            </button>
          )}
        </div>
      ),
    },
  ];

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        await rolesApi.update(editing.id, form);
      } else {
        await rolesApi.create(form);
      }
      notify.success('Saved');
      setModalOpen(false);
      load();
    } catch (err) {
      showApiError(err, 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const builtInCount = data.filter((r) => r.systemRole).length;
  const customCount = data.filter((r) => !r.systemRole).length;

  return (
    <div>
      <PageHeader
        title="Roles & permissions"
        breadcrumbs={[{ label: 'Org', to: '/org' }, { label: 'Roles' }]}
        actions={
          canManageRoles ? (
            <Button
              onClick={() => {
                setEditing(null);
                setForm(empty);
                setModalOpen(true);
              }}
            >
              <AddIcon fontSize="small" /> Custom role
            </Button>
          ) : undefined
        }
      />

      <div className="card mb-4 p-4 text-sm text-slate-600 dark:text-slate-400">
        <p className="font-medium text-slate-800 dark:text-slate-200">{ROLES_HELP.title}</p>
        <p className="mt-1">{ROLES_HELP.body}</p>
        <p className="mt-2">{ROLES_HELP.owner}</p>
        <p className="mt-3 text-xs text-slate-500">
          Your organization has <strong>{builtInCount}</strong> built-in roles and{' '}
          <strong>{customCount}</strong> custom roles.
        </p>
      </div>

      <DataTable columns={columns}
        data={data}
        keyField="id"
        loading={loading}
        search={pagination.search}
        onSearchChange={(v) => {
          pagination.setSearch(v);
          pagination.resetPage();
        }}
        page={pagination.page}
        totalPages={pagination.totalPages}
        totalElements={pagination.totalElements}
        onPageChange={pagination.setPage}
      />

      <Modal
        open={viewOpen}
        onClose={() => setViewOpen(false)}
        title={viewing ? `${viewing.name} — permissions` : 'Role permissions'}
        size="lg"
        footer={
          <Button variant="secondary" onClick={() => setViewOpen(false)}>
            Close
          </Button>
        }
      >
        {viewLoading ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : viewing ? (
          <>
            <p className="mb-3 text-sm text-slate-600 dark:text-slate-400">
              <span className="font-medium">Code:</span> {viewing.code}
              {viewing.systemRole && !isRoleEditable(viewing) && (
                <span className="ml-2 rounded bg-slate-100 px-2 py-0.5 text-xs dark:bg-slate-800">
                  Built-in (read-only)
                </span>
              )}
              {viewing.systemRole && isRoleEditable(viewing) && (
                <span className="ml-2 rounded bg-primary-50 px-2 py-0.5 text-xs text-primary-700 dark:bg-primary-950 dark:text-primary-300">
                  Built-in (permissions editable)
                </span>
              )}
            </p>
            <PermissionList role={viewing} />
          </>
        ) : null}
      </Modal>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={
          editing
            ? editing.code === 'ORGANIZATION_OWNER'
              ? 'Edit organization owner permissions'
              : 'Edit custom role'
            : 'New custom role'
        }
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={save} loading={saving}>
              Save
            </Button>
          </>
        }
      >
        <p className="mb-4 text-xs text-slate-500 dark:text-slate-400">
          Create a custom role when built-in roles do not fit. Master Admin is platform-only and cannot
          be assigned here. Use a preset to start quickly.
        </p>
        <div className="mb-4 flex flex-wrap gap-2">
          {ROLE_PRESET_LABELS.map((p) => (
            <Button key={p.key} variant="secondary" type="button" onClick={() => applyPreset(p.key)}>
              {p.label}
            </Button>
          ))}
        </div>
        <p className="mb-4 text-xs text-slate-400">
          {ROLE_PRESET_LABELS.map((p) => `${p.label}: ${p.hint}`).join(' · ')}
        </p>
        <div className="mb-4 grid gap-4 sm:grid-cols-2">
          <Input
            label="Code *"
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase().replace(/\s+/g, '_') })}
            placeholder="e.g. SUPERVISOR"
            disabled={!!editing}
          />
          <Input
            label="Name *"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="e.g. Floor Supervisor"
          />
        </div>
        <p className="label">Permissions by module</p>
        <div className="max-h-64 space-y-4 overflow-y-auto">
          {permissionGroups.map(([module, perms]) => (
            <div key={module}>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                {module}
              </p>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {perms.map((p) => (
                  <label key={p.id} className="flex items-start gap-2 text-sm">
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={(form.permissionIds ?? []).includes(p.id)}
                      onChange={() => togglePerm(p.id)}
                    />
                    <span>
                      <span className="font-medium text-slate-800 dark:text-slate-200">{p.name}</span>
                      <span className="block text-xs text-slate-500">{p.code}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Modal>
    </div>
  );
}
